import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  Snackbar,
  Alert,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { OrderContext } from "../../contexts/OrderContext";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

const CompleteOrderWithoutQR = () => {
  const navigate = useNavigate();

  const { storeData } = useContext(OrderContext);
  const { state } = useLocation();
  const { order, completionStatus } = state || {};
  const [serialNumber, setSerialNumber] = useState(() => {
    const storedData = localStorage.getItem("qrDataStore");
    const parsedData = storedData ? JSON.parse(storedData) : {};
    return Object.keys(parsedData).length + 1;
  });
  const [isFinalsaveDisable, setIsFinalsaveDisable] = useState(false);

  //States for items details
  const [itemDetails, setItemDetails] = useState({
    totalpcs: "",
    sizeLength: "",
    enterlbl: "",
    labelWeight: "",
    grossWeight: "",
    stoneWeight: "",
    netWeight: "",
  });
  const [stoneDetails, setStoneDetails] = useState({
    type: "",
    description: "",
    stonepcs: "",
    weightPerPcs: "",
    weight: "",
  });
  const [stoneList, setStoneList] = useState([]);

  const [showStoneForm, setShowStoneForm] = useState(false);

  //Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  //Handler Functions----------------
  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleItemChange = (e) => {
    const { name, value } = e.target;
    setItemDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleStoneChange = (e) => {
    const { name, value } = e.target;
    let updated = { ...stoneDetails, [name]: value };

    const pcs = parseFloat(name === "stonepcs" ? value : stoneDetails.stonepcs);
    const perPcs = parseFloat(
      name === "weightPerPcs" ? value : stoneDetails.weightPerPcs
    );

    if (!isNaN(pcs) && !isNaN(perPcs)) {
      updated.weight = (pcs * perPcs).toFixed(3);
    }

    setStoneDetails(updated);
  };

  const handleAddStone = () => {
    if (
      !stoneDetails.type ||
      !stoneDetails.stonepcs ||
      !stoneDetails.weightPerPcs ||
      !stoneDetails.weight
    ) {
      setSnackbar({
        open: true,
        message: "Please fill stone details",
        severity: "error",
      });
      return;
    }

    const newStone = { ...stoneDetails };
    const newList = [...stoneList, newStone];

    const totalStoneWeight = newList.reduce(
      (sum, stone) => sum + parseFloat(stone.weight || 0),
      0
    );

    setStoneList(newList);

    setItemDetails((prev) => ({
      ...prev,
      stoneWeight: totalStoneWeight.toFixed(3),
    }));

    setStoneDetails({
      type: "",
      description: "",
      stonepcs: "",
      weightPerPcs: "",
      weight: "",
    });

    setShowStoneForm(false);
  };

  //Recalculation---------------------
  useEffect(() => {
    calculateWeights(itemDetails);
  }, [
    itemDetails.labelWeight,
    itemDetails.enterlbl,
    itemDetails.stoneWeight,
    itemDetails.grossWeight,
  ]);

  //Calculation function--------------
  const calculateWeights = (data) => {
    const gross = parseFloat(data.grossWeight);
    const lbl = parseFloat(data.enterlbl);
    const stone = parseFloat(data.stoneWeight) || 0;

    let updated = { ...data };

    if (!isNaN(lbl) && !isNaN(data.labelWeight)) {
      updated.grossWeight = (parseFloat(data.labelWeight) - lbl).toFixed(3);
    }

    if (!isNaN(gross)) {
      updated.netWeight = (gross - stone).toFixed(3);
    } else {
      updated.netWeight = "";
    }

    setItemDetails(updated);
  };

  //Item details validation
  const isFormComplete = () => {
    const required = [
      "totalpcs",
      "enterlbl",
      "labelWeight",
      "grossWeight",
      "netWeight",
    ];
    const allFilled = required.every((key) => itemDetails[key]);

    const stoneFilled = showStoneForm
      ? ["type", "stonepcs", "weightPerPcs", "weight"].every(
          (key) => stoneDetails[key]
        )
      : true;

    return allFilled && stoneFilled;
  };

  //Delete stone from stone list
  const handleDeleteStone = (indexToDelete) => {
    const newList = stoneList.filter((_, i) => i !== indexToDelete);
    setStoneList(newList);

    const totalStoneWeight = newList.reduce(
      (sum, stone) => sum + parseFloat(stone.weight || 0),
      0
    );

    setItemDetails((prev) => ({
      ...prev,
      stoneWeight: totalStoneWeight.toFixed(3),
    }));
  };

  //Edit stone
  const handleEditStone = (index) => {
    const stoneToEdit = stoneList[index];
    setStoneDetails(stoneToEdit);
    setShowStoneForm(true);
    const updatedList = stoneList.filter((_, i) => i !== index);
    setStoneList(updatedList);
  };

  //Stone weight calculation
  useEffect(() => {
    const totalStoneWeight = stoneList.reduce(
      (sum, stone) => sum + parseFloat(stone.weight || 0),
      0
    );
    setItemDetails((prev) => ({
      ...prev,
      stoneWeight: totalStoneWeight.toFixed(3),
    }));
  }, [stoneList]);

  //Final save handler function
  const handleFinalSave = async () => {
    if (!isFormComplete()) {
      setSnackbar({
        open: true,
        message: "Please fill all required fields before saving.",
        severity: "error",
      });
      setIsFinalsaveDisable(false);

      return;
    }
    setIsFinalsaveDisable(true);
    try {
      const token = localStorage.getItem("authToken");
      console.log(token);

      if (!token) {
        setSnackbar({
          open: true,
          message: "Unauthorized! Please login...",
          severity: "error",
        });
        setIsFinalsaveDisable(false);

        return;
      }
      if (!order?.storeorderno || !order?.orderid) {
        setSnackbar({
          open: true,
          message: "Missing order details. Please try again.",
          severity: "error",
        });
        setIsFinalsaveDisable(false);

        return;
      }

      const pcsDetails = {
        enterlbl: itemDetails.enterlbl,
        totalpcs: itemDetails.totalpcs,
        sizeLength: itemDetails.sizeLength,
        grossWeight: parseFloat(itemDetails.grossWeight),
        labelWeight: itemDetails.labelWeight,
        stoneWeight: parseFloat(itemDetails.stoneWeight),
        netWeight: parseFloat(itemDetails.netWeight),
      };

      const formattedStoneList = stoneList.map((stone) => ({
        type: stone.type,
        description: stone.description,
        stonepcs: stone.stonepcs,
        weightPerPcs: stone.weightPerPcs,
        weight: stone.weight,
      }));

      const payload = {
        [order.storeorderno]: {
          pcsDetails,
          stoneList: formattedStoneList,
        },
        storeorderno: order.storeorderno,
        orderid: order.orderid,
        completionStatus: completionStatus,
      };

      //Api call

      const response = await axios.post(
        ` ${process.env.REACT_APP_API_BASE_URL}/api/orders/kariagarwithoutqr`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      console.log(response);
      if (response?.data?.success) {
        setSnackbar({
          open: true,
          message: "Order saved successfully!",
          severity: "success",
        });

        setItemDetails({
          totalpcs: "",
          sizeLength: "",
          enterlbl: "",
          labelWeight: "",
          grossWeight: "",
          stoneWeight: "",
          netWeight: "",
        });
        setStoneDetails({
          type: "",
          description: "",
          stonepcs: "",
          weightPerPcs: "",
          weight: "",
        });
        setStoneList([]);
        setTimeout(() => {
          navigate("/home");
        }, 1000);
      } else {
        setIsFinalsaveDisable(false);

        setSnackbar({
          open: true,
          message: response?.data?.message,
          severity: "error",
        });
      }
    } catch (error) {
      setIsFinalsaveDisable(false);

      console.error("Error in saving order:", error);
      setSnackbar({
        open: true,
        message: "Failed to save order. Please try again.",
        severity: "error",
      });
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 1, mb: 1 }}>
      <Paper elevation={3} sx={{ p: 3, textAlign: "center" }}>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <Typography variant="h6">SL.NO</Typography>
            {serialNumber}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="h6">Order No</Typography>
            {order?.storeorderno}
          </Grid>
        </Grid>

        {/* Form Inputs */}
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="PCS"
                name="totalpcs"
                type="number"
                value={itemDetails.totalpcs}
                onChange={handleItemChange}
                fullWidth
                inputMode="numeric"
                required
                inputProps={{ min: 0, step: "any" }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Size/Length"
                name="sizeLength"
                type="number"
                value={itemDetails.sizeLength}
                onChange={handleItemChange}
                fullWidth
                inputProps={{ min: 0, step: "any" }}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Label Weight"
                name="enterlbl"
                type="number"
                value={itemDetails.enterlbl}
                onChange={handleItemChange}
                fullWidth
                required
                InputLabelProps={{ shrink: !!itemDetails.enterlbl }}
                inputProps={{ min: 0, step: "any" }}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Gross + Label Wt"
                name="labelWeight"
                type="number"
                value={itemDetails.labelWeight}
                onChange={handleItemChange}
                fullWidth
                required
                inputProps={{ min: 0, step: "any" }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Gross Weight"
                name="grossWeight"
                type="number"
                value={itemDetails.grossWeight}
                onChange={handleItemChange}
                fullWidth
                required
                inputProps={{ min: 0, step: "any" }}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                disabled
                label="Stone Weight"
                name="stoneWeight"
                type="number"
                value={itemDetails.stoneWeight}
                onChange={handleItemChange}
                fullWidth
                required
                inputProps={{ min: 0, step: "any" }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Net Weight"
                name="netWeight"
                type="number"
                value={itemDetails.netWeight}
                disabled
                fullWidth
                inputProps={{ min: 0, step: "any" }}
              />
            </Grid>
          </Grid>

          {/* Stone Controls */}
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
            <Button
              variant="contained"
              sx={{ fontWeight: "bold", textTransform: "none" }}
              onClick={() => setShowStoneForm(true)}
            >
              Add stone Details
            </Button>
            {showStoneForm && (
              <IconButton onClick={() => setShowStoneForm(false)}>
                <CloseIcon />
              </IconButton>
            )}
          </Box>

          {/* Stone Form */}
          {showStoneForm && (
            <Box sx={{ mt: 2 }}>
              <FormControl fullWidth sx={{ mb: 1 }}>
                <InputLabel id="stone-type-label">Stone Type</InputLabel>
                <Select
                  name="type"
                  labelId="stone-type-label"
                  value={stoneDetails.type || ""}
                  onChange={handleStoneChange}
                  label="Stone Type"
                  MenuProps={{ PaperProps: { sx: { textAlign: "left" } } }}
                  sx={{ textAlign: "left" }}
                >
                  {storeData?.stone?.length > 0 ? (
                    storeData.stone.map((stone) => (
                      <MenuItem key={stone.id} value={stone.Stone_name}>
                        {stone.Stone_name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>No stones available</MenuItem>
                  )}
                </Select>
              </FormControl>

              <TextField
                label="Description"
                name="description"
                value={stoneDetails.description}
                onChange={handleStoneChange}
                fullWidth
                sx={{ mb: 1 }}
              />

              <TextField
                required
                label="PCS"
                name="stonepcs"
                value={stoneDetails.stonepcs}
                onChange={handleStoneChange}
                fullWidth
                sx={{ mb: 1 }}
                type="number"
                inputMode="numeric"
                inputProps={{ min: 0, step: "any" }}
              />

              <TextField
                label="Weight Per PCS"
                name="weightPerPcs"
                type="number"
                required
                value={stoneDetails.weightPerPcs}
                onChange={handleStoneChange}
                fullWidth
                sx={{ mb: 2 }}
                inputProps={{ min: 0, step: "any" }}
              />

              <TextField
                disabled
                required
                label="Stone Weight"
                name="weight"
                type="number"
                value={stoneDetails.weight}
                onChange={handleStoneChange}
                fullWidth
                sx={{ mb: 1 }}
              />

              <Button variant="contained" onClick={handleAddStone}>
                Add
              </Button>
            </Box>
          )}
        </Box>

        {/* Stone Table */}
        <TableContainer sx={{ mt: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>PCS</TableCell>
                <TableCell>W/Pcs</TableCell>
                <TableCell>Weight</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stoneList.length > 0 ? (
                stoneList.map((stone, index) => (
                  <TableRow key={index}>
                    <TableCell>{stone.type}</TableCell>
                    <TableCell>{stone.stonepcs}</TableCell>
                    <TableCell>{stone.weightPerPcs}</TableCell>
                    <TableCell>{stone.weight}</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <IconButton onClick={() => handleEditStone(index)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton onClick={() => handleDeleteStone(index)}>
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No stone details added.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Save & Summary */}
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Button
            variant="contained"
            color="success"
            sx={{ minWidth: 200 }}
            onClick={handleFinalSave}
            disabled={!isFormComplete() || isFinalsaveDisable}
          >
            Final Save
          </Button>
        </Box>

        <Box
          sx={{
            mt: 2,
            p: 2,
            bgcolor: "#f5f5f5",
            borderRadius: 2,
            boxShadow: 2,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
            gap: 2,
            textAlign: "center",
          }}
        >
          {["PCS", "Gross Weight", "Stone Weight", "Net Weight"].map(
            (label, i) => (
              <Box key={label}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: "bold", color: "#555" }}
                >
                  {label}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: "normal", color: "#222" }}
                >
                  {[
                    itemDetails.totalpcs,
                    itemDetails.grossWeight,
                    itemDetails.stoneWeight,
                    itemDetails.netWeight,
                  ][i] || "0.00"}
                </Typography>
              </Box>
            )
          )}
        </Box>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          sx={{
            "&.MuiSnackbar-root": {
              bottom: "100px !important", // force override snackbar position
            },
          }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Paper>
    </Container>
  );
};

export default CompleteOrderWithoutQR;
